<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): see CriterionEnumInput.vue --
         inheritAttrs: false stops Vue 3 from auto-applying class/style to root. -->
    <div class="CriterionMultiEnumInput CriterionEnumInput" :class="$attrs.class" :style="$attrs.style">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <NewDropdown
                    class="CriterionMultiEnumInput__dropdown"
                    label="Select"
                    popup-content-class="CriterionMultiEnumInput__dropdownPopupContent"
                    multi-select
                    enable-clear
                    :options="options"
                    :selected="currentOptions"
                    :disabled="rbacDisabled"
                    @input="onInput"
                    @blur="touch"
                    @focus="onFocus"
                />
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component, Watch, Prop } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { JsonFormArray, JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';

    import AbstractCriterionInput from './AbstractCriterionInput';

    @Component({
        emits: [ 'delete', 'input' ],
        components: {
            EidIcon,
            FormInput,
            FontAwesomeIcon,
            FormElement,
            NewDropdown,
            LockInputIcon,
            NewButton,
        },
        inheritAttrs: false,
    })
    export default class CriterionMultiEnumInput extends AbstractCriterionInput<JsonFormArray, any[]> {
        @Prop()
        protected disallowedValues: any;

        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;
        private options: DropdownOption[] = [];

        @Watch('enumSchema', { immediate: true })
        @Watch('disallowedValues')
        private onEnumChange(): void {
            this.options = this.enumSchema.enum.map((value, i) => ({
                value,
                label: this.getOptionLabel(value, i),
                disable: this.isOptionDisabled(this.disallowedValues as any[], this.value, value),
            }));
        }

        private get enumSchema(): JsonSchemaEnum {
            return (this.form.item as JsonFormBase<JsonSchemaEnum>).schema;
        }

        private get currentOptions(): DropdownOption[] {
            return this.options.filter((option) => this.value.find((val) => val === option.value));
        }

        private getOptionLabel(value: any, index: number): string {
            return this.enumSchema.enumLabels && this.enumSchema.enumLabels.length > 0
                ? this.enumSchema.enumLabels[index]
                : value;
        }

        private isOptionDisabled(disallowedValues: any[], selectedValues: any[], value: any): boolean {
            const isDisallowed = disallowedValues && disallowedValues.some((disallowed) => disallowed === value);
            if (isDisallowed) {
                const isSelected = selectedValues.includes(value);
                return !isSelected;
            }
            return false;
        }

        private buildOptionFromValue(value: any): DropdownOption {
            return { value, label: value };
        }

        private buildOptionFromLabel(value: any, index: number, labels: string[]): DropdownOption {
            return { value, label: labels[index] };
        }

        private onInput(options: DropdownOption[]): void {
            this.$emit('input', options.map((option) => option.value));
        }
    }
</script>
